import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useInfiniteQuotations,
  useQuotation,
  useOpenQuotationPdf,
} from '../../quotations/hooks';
import { CustomSelect } from '@/components';
import { parseApiError } from '@/common/utils';
import { useFeatures } from '@/common/hooks';

function productsCountOf(quotation) {
  if (typeof quotation?.products_count === 'number') {
    return quotation.products_count;
  }
  if (
    quotation?.products_count != null &&
    quotation?.products_count !== '' &&
    !Number.isNaN(Number(quotation.products_count))
  ) {
    return Number(quotation.products_count);
  }
  if (Array.isArray(quotation?.products)) return quotation.products.length;
  return null;
}

function formatTaxRate(taxRate) {
  if (taxRate == null || taxRate === '') return null;
  const n = Number(taxRate);
  if (Number.isNaN(n)) return String(taxRate);
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}%`;
}

/**
 * Inline quotation catalog selector for Flow Builder step editor.
 * Features dynamic debounced API search with infinite on-scroll pagination.
 */
export function QuotationPicker({
  companyId,
  value,
  disabled,
  onChange,
  error = false,
  quotationTitle = null,
}) {
  const { hasFeature } = useFeatures();
  const canManageQuotations = hasFeature('quotations');
  const [search, setSearch] = useState('');

  const queryParams = useMemo(
    () => ({
      is_active: true,
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [search],
  );

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuotations(companyId, queryParams);

  const openPdfMutation = useOpenQuotationPdf();

  const quotations = useMemo(
    () =>
      data?.pages?.flatMap((p) => (Array.isArray(p) ? p : p?.results || [])) ||
      [],
    [data],
  );

  const currentValue = value != null ? String(value) : '';

  const selectedFromList = useMemo(
    () => quotations.find((q) => String(q.id) === currentValue) || null,
    [quotations, currentValue],
  );

  // Fetch individual quotation if value is set but not present in currently loaded pages
  const { data: singleQuotation, isLoading: isLoadingSingle } = useQuotation(
    { companyId, quotationId: currentValue },
    { enabled: Boolean(companyId && currentValue && !selectedFromList) },
  );

  const selected = selectedFromList || singleQuotation || null;
  const count = selected ? productsCountOf(selected) : null;
  const missingSelection = !currentValue;
  const deletedCatalog =
    !!currentValue && !isLoading && !isLoadingSingle && !selected;

  const handlePreviewPdf = (e) => {
    e?.stopPropagation?.();
    if (!selected?.id) return;
    if (!selected.pdf_ready) {
      toast.error('Catalog PDF is not ready yet.');
      return;
    }
    openPdfMutation.mutate(
      { companyId, quotationId: selected.id },
      {
        onError: (err) => {
          toast.error(
            (err?.response?.status === 404
              ? 'PDF has not been generated yet.'
              : parseApiError(err).message) || 'Failed to open PDF.',
          );
        },
      },
    );
  };

  // Filter out catalogs with 0 products so they are not shown in list
  const availableQuotations = useMemo(
    () =>
      quotations.filter((q) => {
        const n = productsCountOf(q);
        return n == null || n > 0;
      }),
    [quotations],
  );

  // When not searching, ensure selected catalog is in the options list
  const displayQuotations = useMemo(() => {
    if (!selected || search.trim()) return availableQuotations;
    const exists = availableQuotations.some(
      (q) => String(q.id) === String(selected.id),
    );
    if (!exists && (productsCountOf(selected) ?? 1) > 0) {
      return [selected, ...availableQuotations];
    }
    return availableQuotations;
  }, [availableQuotations, selected, search]);

  const options = useMemo(() => {
    const list = [];
    if (!search.trim()) {
      list.push({
        value: '',
        label: isLoading ? 'Loading…' : 'Select a product catalog…',
      });
    }
    displayQuotations.forEach((q) => {
      const n = productsCountOf(q);
      const countLabel =
        n == null ? '' : ` • ${n} product${n === 1 ? '' : 's'}`;
      const base = q.quote_code ? `${q.title} (${q.quote_code})` : q.title;
      list.push({
        value: String(q.id),
        label: `${base}${countLabel}`,
      });
    });
    return list;
  }, [displayQuotations, isLoading, search]);

  return (
    <div className="space-y-3 w-full min-w-0 max-w-full">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <label className="block text-xs font-medium text-text-secondary truncate">
          Product Catalog <span className="text-red-500">*</span>
        </label>
        {canManageQuotations && (
          <Link
            to="/quotations"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-medium text-primary hover:underline shrink-0"
          >
            Manage Catalogs
          </Link>
        )}
      </div>

      <CustomSelect
        value={currentValue}
        disabled={disabled}
        onChange={(val) => {
          const id = val ? parseInt(val, 10) : null;
          const match =
            displayQuotations.find((q) => q.id === id) ||
            (selected?.id === id ? selected : null);
          onChange(id, match);
        }}
        options={options}
        placeholder={
          selected && count === 0
            ? `${selected.quote_code ? `${selected.title} (${selected.quote_code})` : selected.title} (0 products)`
            : selected
              ? `${selected.quote_code ? `${selected.title} (${selected.quote_code})` : selected.title}${count != null ? ` • ${count} product${count === 1 ? '' : 's'}` : ''}`
              : isLoading
                ? 'Loading…'
                : 'Select a product catalog…'
        }
        searchable
        searchPlaceholder="Search catalogs…"
        onSearchChange={(q) => setSearch(q)}
        onLoadMore={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={Boolean(hasNextPage)}
        isFetchingMore={isFetchingNextPage}
        isLoading={
          isLoading || (isFetching && !isFetchingNextPage && !!search.trim())
        }
        hideCheckIcon
        textSizeClass="text-body-sm"
        className="w-full min-w-0 h-10"
        buttonClassName={`w-full h-10 flex items-center justify-between rounded-xl bg-white border text-text-primary text-sm focus:outline-none px-3 transition-all ${
          error || deletedCatalog || (selected && count === 0)
            ? 'border-red-400 focus:border-red-500'
            : 'border-border-default focus:border-primary'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        emptyMessage={
          search.trim()
            ? 'No matching catalogs found'
            : quotations.length > 0 && availableQuotations.length === 0
              ? 'No catalogs with products found. Add products to a catalog under Quotations.'
              : 'No active catalogs. Create one under Quotations.'
        }
      />

      {error && missingSelection && (
        <p className="text-[11px] text-red-600 leading-snug break-words">
          Please select a product catalog for this quotation step.
        </p>
      )}

      {deletedCatalog && (
        <div className="flex items-start gap-2 rounded-[8px] bg-red-50 border border-red-200 px-3 py-2.5 text-[11px] text-red-700 leading-snug min-w-0">
          <span className="font-semibold shrink-0">Missing catalog.</span>
          <span className="min-w-0 break-words">
            {quotationTitle
              ? `“${quotationTitle}” no longer exists. Re-assign a catalog.`
              : 'Selected catalog no longer exists. Please re-assign a catalog.'}
          </span>
        </div>
      )}

      {isLoading && currentValue && !selected && (
        <div className="rounded-xl bg-[#F8FAFC] border border-border-default p-3 space-y-2.5 text-left shadow-xs animate-pulse w-full min-w-0">
          <div className="h-3 w-24 bg-gray-200 rounded" />
          <div className="grid grid-cols-3 gap-2 w-full min-w-0">
            <div className="bg-white rounded-lg p-2 border border-border-default/80 h-[52px]" />
            <div className="bg-white rounded-lg p-2 border border-border-default/80 h-[52px]" />
            <div className="bg-white rounded-lg p-2 border border-border-default/80 h-[52px]" />
          </div>
          <div className="w-full h-8 rounded-lg bg-gray-200" />
        </div>
      )}

      {selected && (
        <div className="rounded-xl bg-[#F8FAFC] border border-border-default p-3 space-y-2.5 text-left shadow-xs w-full min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
              Catalog Details
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 w-full min-w-0">
            <div className="bg-white rounded-lg p-2 border border-border-default/80 text-center shadow-[0_1px_2px_rgba(0,0,0,0.02)] min-w-0 overflow-hidden">
              <span className="block text-[10px] uppercase font-medium text-text-muted truncate">
                Currency
              </span>
              <span className="block text-xs font-semibold text-text-primary mt-0.5 truncate">
                {selected.currency || 'INR'}
              </span>
            </div>
            <div className="bg-white rounded-lg p-2 border border-border-default/80 text-center shadow-[0_1px_2px_rgba(0,0,0,0.02)] min-w-0 overflow-hidden">
              <span className="block text-[10px] uppercase font-medium text-text-muted truncate">
                Tax
              </span>
              <span className="block text-xs font-semibold text-text-primary mt-0.5 truncate">
                {formatTaxRate(selected.tax_rate)
                  ? `${formatTaxRate(selected.tax_rate)} GST`
                  : '0% GST'}
              </span>
            </div>
            <div className="bg-white rounded-lg p-2 border border-border-default/80 text-center shadow-[0_1px_2px_rgba(0,0,0,0.02)] min-w-0 overflow-hidden">
              <span className="block text-[10px] uppercase font-medium text-text-muted truncate">
                Products
              </span>
              <span className="block text-xs font-semibold text-text-primary mt-0.5 truncate">
                {count == null ? '—' : `${count} item${count === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>

          <button
            type="button"
            disabled={!selected.pdf_ready || openPdfMutation.isPending}
            onClick={handlePreviewPdf}
            title={
              selected.pdf_ready
                ? 'Preview catalog PDF in a new tab'
                : 'PDF not generated yet'
            }
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-border-default bg-white hover:bg-primary/5 hover:border-primary text-text-primary hover:text-primary transition-all shadow-xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer min-w-0 truncate"
          >
            {openPdfMutation.isPending ? (
              <>
                <svg
                  className="animate-spin h-3.5 w-3.5 text-primary shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="truncate">Opening PDF…</span>
              </>
            ) : (
              <>
                <svg
                  className="w-3.5 h-3.5 text-primary shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
                <span className="truncate">Preview PDF</span>
              </>
            )}
          </button>

          {count === 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-800 leading-snug min-w-0">
              Warning: This catalog has 0 products and cannot generate
              quotations. Add products before activating the flow.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
